export interface ThemeColor {
  id: string;
  name: string;
  hex: string;
  hue: number;
  chromaFactor?: number;
}

export const THEME_COLORS: ThemeColor[] = [
  { id: "purple", name: "Royal Purple", hex: "#7C3AED", hue: 295 },
  { id: "emerald", name: "Emerald Green", hex: "#10B981", hue: 155 },
  { id: "teal", name: "Teal Mint", hex: "#14B8A6", hue: 170 },
  { id: "cyan", name: "Cyan Sky", hex: "#06B6D4", hue: 220 },
  { id: "blue", name: "Royal Blue", hex: "#2563EB", hue: 250 },
  { id: "indigo", name: "Indigo", hex: "#4F46E5", hue: 275 },
  { id: "pink", name: "Hot Pink", hex: "#EC4899", hue: 345 },
  { id: "red", name: "Ruby Red", hex: "#EF4444", hue: 25 },
  { id: "orange", name: "Warm Orange", hex: "#F97316", hue: 50 },
  { id: "amber", name: "Amber Gold", hex: "#F59E0B", hue: 75 },
  { id: "deep_teal", name: "Deep Teal", hex: "#0D9488", hue: 185 },
  { id: "lime", name: "Fresh Lime", hex: "#84CC16", hue: 125 },
  { id: "slate", name: "Slate Steel", hex: "#64748B", hue: 240, chromaFactor: 0.35 },
  { id: "crimson", name: "Crimson", hex: "#BE123C", hue: 10 },
  { id: "electric", name: "Electric Blue", hex: "#0284C7", hue: 235 },
];

export const DEFAULT_THEME_ID = "emerald";

export function applyThemeColor(themeId: string) {
  const theme = THEME_COLORS.find((c) => c.id === themeId) || THEME_COLORS[1]; // default emerald
  const hue = theme.hue;
  const cf = theme.chromaFactor ?? 1;

  const root = document.documentElement;

  // Set oklch primary scale
  root.style.setProperty("--primary-50", `0.97 ${0.02 * cf} ${hue}`);
  root.style.setProperty("--primary-100", `0.93 ${0.04 * cf} ${hue}`);
  root.style.setProperty("--primary-200", `0.88 ${0.06 * cf} ${hue}`);
  root.style.setProperty("--primary-300", `0.80 ${0.08 * cf} ${hue}`);
  root.style.setProperty("--primary-400", `0.72 ${0.10 * cf} ${hue}`);
  root.style.setProperty("--primary-500", `0.52 ${0.14 * cf} ${hue}`);
  root.style.setProperty("--primary-600", `0.46 ${0.13 * cf} ${hue}`);
  root.style.setProperty("--primary-700", `0.40 ${0.12 * cf} ${hue}`);
  root.style.setProperty("--primary-800", `0.34 ${0.10 * cf} ${hue}`);
  root.style.setProperty("--primary-900", `0.28 ${0.08 * cf} ${hue}`);
  root.style.setProperty("--primary-950", `0.22 ${0.07 * cf} ${hue}`);

  // Set oklch secondary scale matching theme hue
  root.style.setProperty("--secondary-50", `0.97 ${0.015 * cf} ${hue}`);
  root.style.setProperty("--secondary-100", `0.92 ${0.03 * cf} ${hue}`);
  root.style.setProperty("--secondary-200", `0.85 ${0.05 * cf} ${hue}`);
  root.style.setProperty("--secondary-300", `0.75 ${0.07 * cf} ${hue}`);
  root.style.setProperty("--secondary-400", `0.65 ${0.09 * cf} ${hue}`);
  root.style.setProperty("--secondary-500", `0.55 ${0.12 * cf} ${hue}`);
  root.style.setProperty("--secondary-600", `0.48 ${0.11 * cf} ${hue}`);
  root.style.setProperty("--secondary-700", `0.40 ${0.09 * cf} ${hue}`);
  root.style.setProperty("--secondary-800", `0.32 ${0.07 * cf} ${hue}`);
  root.style.setProperty("--secondary-900", `0.24 ${0.05 * cf} ${hue}`);
  root.style.setProperty("--secondary-950", `0.16 ${0.03 * cf} ${hue}`);

  // Set oklch accent scale matching theme hue
  root.style.setProperty("--accent-50", `0.97 ${0.03 * cf} ${hue}`);
  root.style.setProperty("--accent-100", `0.93 ${0.05 * cf} ${hue}`);
  root.style.setProperty("--accent-200", `0.88 ${0.07 * cf} ${hue}`);
  root.style.setProperty("--accent-300", `0.80 ${0.10 * cf} ${hue}`);
  root.style.setProperty("--accent-400", `0.72 ${0.12 * cf} ${hue}`);
  root.style.setProperty("--accent-500", `0.56 ${0.14 * cf} ${hue}`);
  root.style.setProperty("--accent-600", `0.48 ${0.13 * cf} ${hue}`);
  root.style.setProperty("--accent-700", `0.40 ${0.12 * cf} ${hue}`);
  root.style.setProperty("--accent-800", `0.32 ${0.10 * cf} ${hue}`);
  root.style.setProperty("--accent-900", `0.24 ${0.08 * cf} ${hue}`);
  root.style.setProperty("--accent-950", `0.16 ${0.06 * cf} ${hue}`);

  try {
    localStorage.setItem("wyzon_theme_color", themeId);
  } catch {
    // Ignore localStorage errors
  }
}

export function getSavedTheme(): ThemeColor {
  try {
    const savedId = localStorage.getItem("wyzon_theme_color");
    const found = THEME_COLORS.find((c) => c.id === savedId);
    if (found) return found;
  } catch {
    // ignore
  }
  return THEME_COLORS[1]; // default emerald
}
